import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Switch,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { useDisplaySettingsStore } from '../../stores/displaySettingsStore';
import { useAuthStore } from '../../stores/authStore';
import { PERMISSIONS } from '../../constants/permissions';
import theme from '../../theme';

export const DisplayOptionsScreen = () => {
  const { user, hasPermission } = useAuthStore();
  const {
    // Vehicle settings
    showVehicleRentalRates,
    showVehicleVIN,
    showVehicleOdometer,
    showVehicleImages,
    vehicleCompactMode,
    
    // Driver settings
    showDriverRates,
    showDriverBalance,
    showDriverPhone,
    showDriverEmiratesId,
    showDriverPhotos,
    driverCompactMode,
    
    // Actions
    loading,
    error,
    loadSettings,
    updateSetting,
    resetToDefaults,
  } = useDisplaySettingsStore();

  const [fadeAnim] = useState(new Animated.Value(0));

  useEffect(() => {
    loadSettings();
    
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, []);

  const canEdit = hasPermission(PERMISSIONS.EDIT_SETTINGS);

  const handleToggle = async (key: string, value: boolean) => {
    if (!canEdit) {
      Alert.alert(
        'Permission Denied',
        'Only administrators can modify display settings.',
        [{ text: 'OK' }]
      );
      return;
    }

    await updateSetting(key as any, value);
  };

  const handleResetToDefaults = () => {
    if (!canEdit) {
      Alert.alert(
        'Permission Denied',
        'Only administrators can reset settings.',
        [{ text: 'OK' }]
      );
      return;
    }

    Alert.alert(
      'Reset to Defaults',
      'Are you sure you want to reset all display settings to their default values? This will show all information on cards.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: resetToDefaults,
        },
      ]
    );
  };

  const renderToggleRow = (
    label: string,
    description: string,
    icon: keyof typeof Ionicons.glyphMap,
    value: boolean,
    settingKey: string,
    color: string = theme.colors.primary
  ) => (
    <View style={styles.toggleRow}>
      <View style={styles.toggleLeft}>
        <View style={[styles.iconContainer, { backgroundColor: color + '20' }]}>
          <Ionicons name={icon} size={24} color={color} />
        </View>
        <View style={styles.toggleText}>
          <Text style={styles.toggleLabel}>{label}</Text>
          <Text style={styles.toggleDescription}>{description}</Text>
        </View>
      </View>
      <Switch
        value={value}
        onValueChange={(newValue) => handleToggle(settingKey, newValue)}
        trackColor={{ false: '#d1d5db', true: color + '60' }}
        thumbColor={value ? color : '#f3f4f6'}
        disabled={!canEdit}
      />
    </View>
  );

  const renderSectionHeader = (
    title: string,
    subtitle: string,
    icon: keyof typeof Ionicons.glyphMap,
    color: string
  ) => (
    <View style={styles.sectionHeader}>
      <View style={[styles.sectionIconContainer, { backgroundColor: color + '20' }]}>
        <Ionicons name={icon} size={28} color={color} />
      </View>
      <View style={styles.sectionTextContainer}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <Text style={styles.sectionSubtitle}>{subtitle}</Text>
      </View>
    </View>
  );

  if (loading && !showVehicleRentalRates) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading display settings...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Animated.View style={{ opacity: fadeAnim }}>
        {/* Header */}
        <View style={styles.header}>
          <Ionicons name="options-outline" size={48} color={theme.colors.primary} />
          <Text style={styles.headerTitle}>Display Options</Text>
          <Text style={styles.headerSubtitle}>
            Customize what information appears on vehicle and driver cards
          </Text>
          {!canEdit && (
            <View style={styles.permissionBanner}>
              <Ionicons name="lock-closed" size={16} color={theme.colors.warning} />
              <Text style={styles.permissionText}>View only - Admin access required to edit</Text>
            </View>
          )}
        </View>

        {error && (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* Vehicle Card Settings */}
        <Card style={styles.section}>
          {renderSectionHeader(
            'Vehicle Cards',
            'Control what information is displayed on vehicle cards',
            'car-sport',
            '#3b82f6'
          )}

          <View style={styles.divider} />

          {renderToggleRow(
            'Show Rental Rates',
            'Display daily/monthly rental rates',
            'cash-outline',
            showVehicleRentalRates,
            'showVehicleRentalRates',
            '#10b981'
          )}

          {renderToggleRow(
            'Show VIN/Chassis',
            'Display vehicle identification numbers',
            'barcode-outline',
            showVehicleVIN,
            'showVehicleVIN',
            '#6366f1'
          )}

          {renderToggleRow(
            'Show Odometer',
            'Display current mileage/odometer reading',
            'speedometer-outline',
            showVehicleOdometer,
            'showVehicleOdometer',
            '#8b5cf6'
          )}

          {renderToggleRow(
            'Show Vehicle Images',
            'Display vehicle photos on cards',
            'image-outline',
            showVehicleImages,
            'showVehicleImages',
            '#ec4899'
          )}

          {renderToggleRow(
            'Compact Mode',
            'Reduce card size for more vehicles per screen',
            'contract-outline',
            vehicleCompactMode,
            'vehicleCompactMode',
            '#f59e0b'
          )}
        </Card>

        {/* Driver Card Settings */}
        <Card style={styles.section}>
          {renderSectionHeader(
            'Driver Cards',
            'Control what information is displayed on driver cards',
            'people',
            '#06b6d4'
          )}

          <View style={styles.divider} />

          {renderToggleRow(
            'Show Rates & Commission',
            'Display salary and commission percentages',
            'cash-outline',
            showDriverRates,
            'showDriverRates',
            '#10b981'
          )}

          {renderToggleRow(
            'Show Balance',
            'Display driver account balance',
            'wallet-outline',
            showDriverBalance,
            'showDriverBalance',
            '#ef4444'
          )}

          {renderToggleRow(
            'Show Phone Numbers',
            'Display driver contact numbers',
            'call-outline',
            showDriverPhone,
            'showDriverPhone',
            '#3b82f6'
          )}

          {renderToggleRow(
            'Show Emirates ID',
            'Display Emirates ID numbers',
            'card-outline',
            showDriverEmiratesId,
            'showDriverEmiratesId',
            '#8b5cf6'
          )}

          {renderToggleRow(
            'Show Driver Photos',
            'Display driver profile photos',
            'image-outline',
            showDriverPhotos,
            'showDriverPhotos',
            '#ec4899'
          )}

          {renderToggleRow(
            'Compact Mode',
            'Reduce card size for more drivers per screen',
            'contract-outline',
            driverCompactMode,
            'driverCompactMode',
            '#f59e0b'
          )}
        </Card>

        {/* Quick Stats */}
        <Card style={styles.statsCard}>
          <View style={styles.statsHeader}>
            <Ionicons name="stats-chart" size={24} color={theme.colors.primary} />
            <Text style={styles.statsTitle}>Current Configuration</Text>
          </View>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {[showVehicleRentalRates, showVehicleVIN, showVehicleOdometer, showVehicleImages].filter(Boolean).length}/4
              </Text>
              <Text style={styles.statLabel}>Vehicle Fields Visible</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {[showDriverRates, showDriverBalance, showDriverPhone, showDriverEmiratesId, showDriverPhotos].filter(Boolean).length}/5
              </Text>
              <Text style={styles.statLabel}>Driver Fields Visible</Text>
            </View>
          </View>
        </Card>

        {/* Reset Button */}
        {canEdit && (
          <TouchableOpacity
            style={styles.resetButton}
            onPress={handleResetToDefaults}
            activeOpacity={0.7}
          >
            <Ionicons name="refresh-outline" size={20} color={theme.colors.error} />
            <Text style={styles.resetButtonText}>Reset to Defaults</Text>
          </TouchableOpacity>
        )}

        {/* Info Banner */}
        <View style={styles.infoBanner}>
          <Ionicons name="information-circle-outline" size={20} color={theme.colors.info} />
          <Text style={styles.infoText}>
            Changes apply immediately to all users across the system. License plates and driver names always remain visible.
          </Text>
        </View>
      </Animated.View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainer: {
    padding: theme.spacing.lg,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  header: {
    alignItems: 'center',
    marginBottom: theme.spacing.xl,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
  },
  headerSubtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xl,
  },
  permissionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.warning + '20',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  permissionText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.warning,
    fontWeight: '600',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.error + '20',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    gap: theme.spacing.sm,
  },
  errorText: {
    flex: 1,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.error,
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionIconContainer: {
    width: 56,
    height: 56,
    borderRadius: theme.borderRadius.lg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
  },
  sectionTextContainer: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.border,
    marginBottom: theme.spacing.md,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border + '40',
  },
  toggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: theme.spacing.md,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
  },
  toggleText: {
    flex: 1,
  },
  toggleLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  toggleDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  statsCard: {
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    backgroundColor: theme.colors.primary + '10',
  },
  statsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  statsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  statsGrid: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: 4,
    textAlign: 'center',
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: theme.colors.border,
    marginHorizontal: theme.spacing.md,
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.error + '10',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    gap: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.error + '40',
  },
  resetButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600',
    color: theme.colors.error,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: theme.colors.info + '20',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.xl,
  },
  infoText: {
    flex: 1,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.info,
    lineHeight: 20,
  },
});
